import {
  BasicButton,
  ButtonGroup,
  DialogContent,
  DialogFooter,
  DialogHeader,
  PrimaryButton,
  RequirementsList,
  Stack,
} from "@certn/cascadia";
import { getLocalTimeZone, today } from "@internationalized/date";
import { Trans, useTranslation } from "react-i18next";
import { GapDocumentationItem } from "./types";
import { Formik } from "formik";
import {
  FileUpload,
  Select,
  SelectItem,
  DatePickerFormikField,
} from "@certn/cascadia-react";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { dataToFile, fileToData } from "../../utils";
import { JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { DEFAULT_FILE_TYPES } from "@certn/utils";
import { object, string } from "yup";

type GapDocumentationModalProps = {
  close: () => void;
  schema: Record<string, JsonSchema4 | JsonSchema7>;
  submit: (document: GapDocumentationItem) => void;
  existingGapDocumentFiles: string[];
};

export const GapDocumentationModal = ({
  close,
  schema,
  submit,
  existingGapDocumentFiles,
}: GapDocumentationModalProps) => {
  const { t } = useTranslation();
  const { generatePresignedUrlEndpoint, caseId } = useApplicationFormContext();

  const gapDocumentTypes = schema.document_type.oneOf ?? [];
  const gapDocumentTypeValues = gapDocumentTypes.map((type) => type.const);

  const validationSchema = object({
    document_type: string()
      .oneOf(gapDocumentTypeValues, t("Invalid document type"))
      .required(t("Document type is required")),
    document_date: string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        t("Document date must be in YYYY-MM-DD format")
      )
      .required(t("Document date is required")),
    gap_document_file: string()
      .required(t("File is required"))
      .test("unique-file", t("This file has already been uploaded"), (key) => {
        if (!key) return true; // required will handle empty

        const name = dataToFile(key)?.name;

        return !existingGapDocumentFiles.some(
          (f) => dataToFile(f)?.name === name
        );
      }),
  });

  const addDocument = (values: Partial<GapDocumentationItem>) => {
    const document_type = values.document_type;
    const document_date = values.document_date;
    const gap_document_file = values.gap_document_file;

    if (!document_type || !document_date || !gap_document_file) {
      throw new Error(
        t(
          "Missing required fields: Document Type, Document Date or Uploaded Document."
        )
      );
    }

    submit({ document_type, document_date, gap_document_file });
    close();
  };

  return (
    <Formik<Partial<GapDocumentationItem>>
      enableReinitialize
      validateOnMount
      initialValues={{
        document_type: undefined,
        document_date: undefined,
        gap_document_file: undefined,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        addDocument(values);
      }}
    >
      {({ values, handleSubmit, isValid, setValues, dirty, errors }) => {
        const gap_document_file_key = values.gap_document_file;
        const gap_document_file = dataToFile(gap_document_file_key);

        return (
          <div>
            <DialogHeader onClose={close}>{t("Add document")}</DialogHeader>
            <DialogContent>
              <Stack gap="space-4">
                <Select
                  testID="gap-doc-document-type"
                  label={
                    schema.document_type.title || t("Select document type")
                  }
                  isRequired
                  value={values.document_type}
                  onChange={(key) => {
                    if (key)
                      setValues((prev) => ({
                        ...prev,
                        document_type: key.toString(),
                      }));
                  }}
                >
                  {gapDocumentTypes.map((option: JsonSchema4 | JsonSchema7) => (
                    <SelectItem
                      key={option.const}
                      id={option.const}
                      textValue={option.title}
                    >
                      {option.title}
                    </SelectItem>
                  ))}
                </Select>
                <DatePickerFormikField
                  isFullWidth
                  testID="gap-doc-document-date"
                  name="document_date"
                  label={schema.document_date.title || t("Date of document")}
                  isRequired
                  maxValue={today(getLocalTimeZone())}
                  value={values.document_date}
                  description={schema.document_date.description}
                />
                <FileUpload
                  isRequired
                  caseId={caseId}
                  requestUrl={generatePresignedUrlEndpoint}
                  bucketDirectory="applicant_uploads"
                  uploadType="GAP_DOCUMENT"
                  maxSizeBytes={10 * 1024 * 1024}
                  multiple={false}
                  files={gap_document_file ? [gap_document_file] : []}
                  setFiles={(newFiles) => {
                    const file = newFiles[0];
                    const key = fileToData(file) ?? undefined;

                    setValues((prev) => ({
                      ...prev,
                      gap_document_file: key,
                    }));
                  }}
                  allowedFileTypes={DEFAULT_FILE_TYPES}
                />
                {gap_document_file && errors.gap_document_file && (
                  <RequirementsList
                    requirements={[
                      {
                        text: errors.gap_document_file,
                        style: "error",
                      },
                    ]}
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogFooter>
              <ButtonGroup>
                <BasicButton size="medium" onPress={close}>
                  <Trans>Cancel</Trans>
                </BasicButton>
                <PrimaryButton
                  size="medium"
                  onPress={handleSubmit}
                  isDisabled={!isValid || !dirty}
                >
                  <Trans>Save</Trans>
                </PrimaryButton>
              </ButtonGroup>
            </DialogFooter>
          </div>
        );
      }}
    </Formik>
  );
};
